import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, Crop as CropIcon, Trash2, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export const PRESET_ASPECT = {
  hero: 16 / 9,
  card: 3 / 2,
  og: 1200 / 630,
  ba: 4 / 3,
  blog: 16 / 9,
  category: 3 / 2,
  gallery: 1,
  portrait: 4 / 5,
  vertical: 9 / 16,
};

const PRESET_LABEL = {
  hero: "16:9 (1600×900)",
  card: "3:2 (1200×800)",
  og: "1.91:1 (1200×630)",
  ba: "4:3 (1200×900)",
  blog: "16:9 (1600×900)",
  category: "3:2 (1200×800)",
  gallery: "1:1 (1000×1000)",
  portrait: "4:5 dikey (1080×1350)",
  vertical: "9:16 dikey (1080×1920)",
};

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

async function getCroppedBlob(src, area) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.95));
}

function CropModal({ src, preset, onCancel, onDone }) {
  const aspect = PRESET_ASPECT[preset] || 16 / 9;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);

  async function confirm() {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const fd = new FormData();
      fd.append("file", blob, "upload.jpg");
      fd.append("preset", preset);
      const { data } = await api.post("/admin/upload-image", fd);
      onDone(data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Görsel yüklenemedi.");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black/90 p-4 backdrop-blur-sm" data-testid="image-crop-modal">
      <div className="mx-auto flex w-full max-w-3xl items-center justify-between py-3">
        <span className="flex items-center gap-2 font-oswald text-sm uppercase tracking-[0.15em] text-gold"><CropIcon className="h-4 w-4" /> Görseli Kırp — {PRESET_LABEL[preset] || preset}</span>
        <button onClick={onCancel} className="rounded-lg p-2 text-neutral-400 hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative mx-auto h-[55vh] w-full max-w-3xl overflow-hidden border border-white/10 bg-neutral-900">
        <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
      </div>
      <div className="mx-auto mt-4 flex w-full max-w-3xl items-center gap-4">
        <span className="text-xs text-neutral-400">Yakınlaştır</span>
        <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#caa64a]" data-testid="crop-zoom" />
        <button onClick={onCancel} className="border border-white/15 px-4 py-2 text-sm text-white hover:border-white/40">İptal</button>
        <button onClick={confirm} disabled={busy} data-testid="crop-confirm" className="inline-flex items-center gap-2 bg-gold px-5 py-2 font-oswald text-sm font-bold uppercase tracking-[0.08em] text-black hover:bg-gold-300 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Kes ve Yükle
        </button>
      </div>
    </div>
  );
}

function useCropFlow(preset, onResult) {
  const [src, setSrc] = useState(null);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Lütfen bir görsel dosyası seçin.");
      return;
    }
    setSrc(await readFile(file));
  };
  const modal = src ? (
    <CropModal src={src} preset={preset} onCancel={() => setSrc(null)} onDone={(url) => { setSrc(null); onResult(url); }} />
  ) : null;
  return { onPick, modal };
}

export function ImageUploadField({ value, onChange, preset = "hero", label, testid }) {
  const { onPick, modal } = useCropFlow(preset, onChange);
  const inputId = `upl-${testid || label}`;
  return (
    <div className="sm:col-span-2" data-testid={testid}>
      <label className="mb-1.5 block text-xs font-medium text-neutral-400">{label} <span className="text-neutral-600">· {PRESET_LABEL[preset]}</span></label>
      <div className="flex items-center gap-4">
        <div className="flex h-24 w-36 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-neutral-900">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <span className="text-[11px] text-neutral-600">Görsel yok</span>}
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor={inputId} data-testid={`${testid}-btn`} className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-gold px-4 py-2.5 text-sm font-bold text-black hover:bg-gold-300 transition-colors">
            <UploadCloud className="h-4 w-4" /> {value ? "Değiştir" : "Görsel Yükle"}
          </label>
          <input id={inputId} type="file" accept="image/*" className="hidden" onChange={onPick} />
          {value && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-red-400">
              <Trash2 className="h-3.5 w-3.5" /> Kaldır
            </button>
          )}
        </div>
      </div>
      {modal}
    </div>
  );
}

export function ImageListField({ value = [], onChange, preset = "gallery", label, testid }) {
  const list = Array.isArray(value) ? value : [];
  const { onPick, modal } = useCropFlow(preset, (url) => onChange([...list, url]));
  const inputId = `upl-${testid || label}`;
  return (
    <div className="sm:col-span-2" data-testid={testid}>
      <label className="mb-1.5 block text-xs font-medium text-neutral-400">{label} <span className="text-neutral-600">· {PRESET_LABEL[preset]}</span></label>
      <div className="flex flex-wrap gap-3">
        {list.map((url, i) => (
          <div key={i} className="group relative h-20 w-20 overflow-hidden rounded-lg border border-white/10">
            <img src={url} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded bg-black/70 p-1 text-neutral-200 opacity-0 transition group-hover:opacity-100 hover:text-red-400">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <label htmlFor={inputId} data-testid={testid || "imagelist-upload"} className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 text-neutral-400 hover:border-gold/50 hover:text-gold">
          <Plus className="h-5 w-5" /> <span className="text-[10px]">Ekle</span>
        </label>
        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>
      {modal}
    </div>
  );
}
